/**
 * The Google Calendar event an inspection becomes.
 *
 * ADR 0035 removed the "Add to Google Calendar" pop-up and left the other half
 * undone: the pop-up put the appointment on the calendar of whoever happened to
 * be holding the browser, and only if they remembered to press Save in the tab
 * it opened. This is what replaces it.
 *
 * Everything here is a pure function of the job. Nothing talks to Google — the
 * `calendar-sync` Edge Function does that, because it is the only thing allowed
 * near a refresh token. What lives here is the part that is worth being sure
 * about: what the event says, who is on it, and when it starts and ends.
 */

import { jobAddress } from "./search.js";
import type { Assignee } from "./inspection.js";
import type { Job } from "./types.js";

/** What Google is asked to create, in the shape its API takes. */
export interface CalendarEvent {
  summary: string;
  description: string;
  location: string;
  start: { dateTime: string };
  end: { dateTime: string };
  attendees: { email: string }[];
}

/** How long an inspection is assumed to take when nobody has said. */
export const DEFAULT_INSPECTION_MINUTES = 60;

/**
 * Who should be invited, and who cannot be.
 *
 * A crew is a row in `crews` and may have no email address at all — plenty of
 * them are a phone number and a name. Somebody with no address is not an error
 * and is not dropped silently either: they come back in `uninvited` so the
 * screen can say "Pablo's Crew has no email, so they were not invited" rather
 * than leaving somebody to find out by not turning up.
 */
export function inspectionInvitees(
  assignees: readonly (Assignee & { email?: string | null })[],
): { invited: string[]; uninvited: string[] } {
  const invited: string[] = [];
  const uninvited: string[] = [];

  for (const assignee of assignees) {
    const email = (assignee.email ?? "").trim();
    // Deliberately not a full address validation. Google will refuse what it
    // cannot deliver to, and its answer is better than a regex's.
    if (email.includes("@")) invited.push(email);
    else uninvited.push(assignee.name);
  }
  // One person on an inspection twice — assigned in both slots — would
  // otherwise be invited twice, which Google accepts and a human queries.
  return { invited: [...new Set(invited)], uninvited };
}

/**
 * When it ends.
 *
 * Kept as a separate function because the duration is the one number on the
 * scheduling form that has never been stored: it is asked for, used to size the
 * event, and thrown away. That is fine — an inspection that runs long does not
 * need the database to know — but it means every caller has to supply it, and a
 * missing one must not produce an event that ends before it starts.
 */
export function inspectionEnd(startIso: string, minutes: number): string {
  const start = new Date(startIso);
  const safe =
    Number.isFinite(minutes) && minutes > 0
      ? minutes
      : DEFAULT_INSPECTION_MINUTES;
  return new Date(start.getTime() + safe * 60_000).toISOString();
}

/**
 * The event itself.
 *
 * The description carries what somebody standing on a driveway needs and
 * nothing they would have to open the app to get: who the customer is, how to
 * ring them, what kind of bid it is, and the job number. It deliberately does
 * not carry a price. A calendar entry is forwarded, screenshotted and read over
 * shoulders, and what a job is worth is not something to put on one.
 */
export function inspectionEvent(input: {
  job: Pick<
    Job,
    "job_number" | "bid_type" | "inspection_at" | "inspection_notes"
  > &
    Partial<Pick<Job, "address" | "city" | "state" | "zip" | "contact">>;
  minutes: number;
  invitees: readonly string[];
}): CalendarEvent | null {
  const { job } = input;
  // No date, no event. Clearing the date is how an inspection is unscheduled,
  // and the caller uses this null to know to delete rather than update.
  if (!job.inspection_at) return null;

  const contact = job.contact ?? {};
  const customer = contact.full_name ?? "";
  const phone = contact.cell ?? contact.phone ?? "";

  const description = [
    job.inspection_notes ?? "",
    "",
    customer ? `Customer: ${customer}` : "",
    phone ? `Phone: ${phone}` : "",
    `Bid: ${job.bid_type}`,
    `Job #: ${job.job_number}`,
  ]
    .filter((line, index) => line !== "" || index === 1)
    .join("\n")
    .trim();

  return {
    summary: `Roof Inspection — ${customer || `Job #${job.job_number}`}`,
    description,
    location: jobAddress(job as Job),
    start: { dateTime: job.inspection_at },
    end: { dateTime: inspectionEnd(job.inspection_at, input.minutes) },
    attendees: input.invitees.map((email) => ({ email })),
  };
}
